import {provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer} from '@angular/core';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling} from '@angular/router';
import {registerLocaleData} from '@angular/common';
import localePt from '@angular/common/locales/pt';
import Aura from '@primeng/themes/aura';
import {providePrimeNG} from 'primeng/config';
import {appRoutes} from './app.routes';
import {environment} from './environments/environment';
import Keycloak from 'keycloak-js';

import {
    AutoRefreshTokenService,
    INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    includeBearerTokenInterceptor,
    provideKeycloak,
    UserActivityService,
    withAutoRefreshToken
} from 'keycloak-angular';
import {buildBearerTokenConditions} from "@/shared/config/build-bearer-token-conditions";
import {ptBR} from "@/shared/config/primeng-ptBR";
import {PlotlyModule} from "angular-plotly.js";
import * as PlotlyJS from 'plotly.js-dist-min';
import {provideAnalytics} from '@/shared/analytics';

PlotlyModule.plotlyjs = PlotlyJS;

// Registrar dados de localização pt-BR
registerLocaleData(localePt);

const conditions = buildBearerTokenConditions(
    environment.keycloak.config.url,   // SSO
    environment.apiUrl,                // API principal
    environment.apiGEEIntegrator,      // Integrador GEE
);

export const provideKeycloakAngular = () =>
    provideKeycloak({
        config: environment.keycloak.config,
        features: [
            withAutoRefreshToken({
                onInactivityTimeout: 'logout',
                sessionTimeout: 60_000
            })
        ],
        providers: [AutoRefreshTokenService, UserActivityService]
    });

export const appConfig: ApplicationConfig = {
    providers: [
        // Configurar locale pt-BR
        { provide: LOCALE_ID, useValue: 'pt' },
        provideKeycloakAngular(),
        provideAppInitializer(() => {
            const kc = inject(Keycloak);
            // Preservar URL original para restaurar após login
            const currentPath = window.location.pathname + window.location.search;
            if (currentPath && currentPath !== '/' && !currentPath.startsWith('/auth')) {
                sessionStorage.setItem('returnUrl', currentPath);
            }
            return kc.init({
                onLoad: 'login-required',
                flow: 'standard',
                pkceMethod: 'S256',
                redirectUri: window.location.origin + '/auth/access',
                checkLoginIframe: false
            } as any).catch((error) => {
                console.error('ERRO: ', error)
                window.location.href = '/auth/error-auth';
            })
        }),
        {
            provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
            useValue: conditions
        },
        provideHttpClient(
            withInterceptors([includeBearerTokenInterceptor]),
            withFetch()
        ),
        provideRouter(appRoutes, withInMemoryScrolling({
            anchorScrolling: 'enabled',
            scrollPositionRestoration: 'enabled'
        }),
        withEnabledBlockingInitialNavigation()),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: '.app-dark'
                }
            },
            inputVariant: 'filled',
            translation: ptBR
        }),
        importProvidersFrom(PlotlyModule),
        provideAnalytics(environment.analytics),
    ]
};
